import { useState, useEffect, useCallback } from 'react';
import type { ThemeMode } from '../types';

// Create a function to get the initial theme
const getInitialTheme = (): ThemeMode => {
  // Check if we're in a browser environment
  if (typeof window === 'undefined') return 'system';

  // Check for stored preference
  const storedTheme = localStorage.getItem('theme') as ThemeMode | null;
  
  if (storedTheme && ['light', 'dark', 'system'].includes(storedTheme)) {
    return storedTheme;
  }

  // Default to system preference
  return 'system';
};

// Convert theme mode to boolean dark mode value
const resolveThemeValue = (theme: ThemeMode): boolean => {
  if (theme === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  }
  
  return theme === 'dark';
};

export const useTheme = () => {
  const [theme, setThemeState] = useState<ThemeMode>(getInitialTheme);
  const [isDark, setIsDark] = useState<boolean>(() => resolveThemeValue(getInitialTheme()));
  
  // Handle system preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = () => {
      if (theme === 'system') {
        setIsDark(mediaQuery.matches);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);
  
  // Apply theme change to DOM and localStorage
  useEffect(() => {
    const newIsDark = resolveThemeValue(theme);
    setIsDark(newIsDark);
    
    if (newIsDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    
    localStorage.setItem('theme', theme);
  }, [theme]);
  
  // Expose a simpler API for changing theme
  const setTheme = useCallback((newTheme: ThemeMode) => {
    setThemeState(newTheme);
  }, []);
  
  return { theme, setTheme, isDark };
};